<div mat-dialog-title class="flex flex-row justify-between content-center items-center">
  <h2 class="text-xl font-semibold text-gray-800">Recover your Username</h2>
  <button [mat-dialog-close]="false" mat-icon-button>
    <mat-icon>close</mat-icon>
  </button>
</div>

<mat-horizontal-stepper [linear]="true" #stepper>
  <!-- Step 1: Enter Contact Information -->
  <mat-step [stepControl]="contactForm">
    <form [formGroup]="contactForm" class="p-5 gap-3 flex flex-col w-full">
      <ng-template matStepLabel>Contact Information</ng-template>
      <div class="mt-6 mb-4">
        <p class="text-lg font-medium text-center text-gray-800">
          <strong> We need to verify your identity to help you recover your username.</strong>
        </p>
        <p class="text-gray-600 text-center mt-2">Please choose how you'd like to receive the verification code.</p>
      </div>

      <div class="p-5 flex flex-col w-full">
        <mat-radio-group
          formControlName="contactMethod"
          (change)="onContactMethodChange($event.value)"
          class="mb-3"
          color="primary">
          <mat-radio-button value="phone" class="mr-7"
            ><span class="text-gray-600 font-medium">Text Message (SMS)</span>
            <mat-icon class="ml-2 text-gray-500">sms</mat-icon></mat-radio-button
            >
            <mat-radio-button value="email"
              ><span class="text-gray-500 font-medium">Email</span>
              <mat-icon class="ml-2 text-gray-500">email</mat-icon></mat-radio-button
              >
            </mat-radio-group>

            @if (contactForm.get('contactMethod')?.value === phoneType) {
              <mat-form-field class="w-full">
                <mat-label>Mobile Phone Number</mat-label>
                <input
                  matInput
                  formControlName="phone"
                  type="tel"
                  mask="(000) 000-0000"
                  [dropSpecialCharacters]="false"
                  (keydown.enter)="$event.preventDefault()" />
                @if (contactForm.get('phone')?.hasError('required')) {
                  <mat-error> Phone number is required. </mat-error>
                }
                @if (contactForm.get('phone')?.hasError('mask')) {
                  <mat-error>
                    Enter a valid phone number. E.g. (123) 456-7890
                  </mat-error>
                }
                @if (contactForm.get('phone')?.hasError('notFound')) {
                  <mat-error
                    >The phone number you entered was not found. Please try again using your email address or contact<a
                    [routerLink]="['contact']"
                    class="text-blue-500 underline hover:text-blue-700"
                    mat-dialog-close="true">
                    customer support</a
                    >.
                  </mat-error>
                }
              </mat-form-field>
            }

            @if (contactForm.get('contactMethod')?.value === emailType) {
              <mat-form-field class="w-full">
                <mat-label>Email Address</mat-label>
                <input matInput formControlName="email" type="email" (keydown.enter)="$event.preventDefault()" />
                @if (contactForm.get('email')?.hasError('required')) {
                  <mat-error> Email is required. </mat-error>
                }
                @if (contactForm.get('email')?.hasError('pattern')) {
                  <mat-error> Enter a valid email address. </mat-error>
                }
                @if (contactForm.get('email')?.hasError('notFound')) {
                  <mat-error
                    >The email address you entered was not found. Please try again using your phone number or contact
                    <a [routerLink]="['contact']" class="text-blue-500 underline hover:text-blue-700" mat-dialog-close="true">
                      customer support</a
                      >.
                    </mat-error>
                  }
                </mat-form-field>
              }

              @defer (on interaction (btnSendVerificationCode)) {
              @if (isLoading) {
                <app-loading class="my-2"></app-loading>
              } @else {
                <app-response-viewer
                  class="my-2"
                  [responseData]="verificationCodeResponse()"
                [dialogRef]="dialogRef"></app-response-viewer>
              }
            }
          </div>
          <div class="flex justify-end gap-3">
            <button mat-raised-button matDialogClose="true">Back to Login</button>
            <button
              mat-raised-button
              #btnSendVerificationCode
              color="primary"
              (click)="onSendVerificationCode(stepper)"
              [disabled]="contactForm.invalid || isLoading || verificationCodeResponse()?.status">
              Send Verification Code
            </button>
          </div>
        </form>
      </mat-step>

      <mat-step [stepControl]="retrieveUserNameForm">
        <form [formGroup]="retrieveUserNameForm" class="p-2">
          <ng-template matStepLabel>Verification Code</ng-template>
          <div class="p-3 flex flex-col w-full">
            <p class="label">Enter your verification code:</p>
            <mat-form-field>
              <mat-label>Verification Code</mat-label>
              <input
                matInput
                formControlName="verificationCode"
                mask="000000"
                placeholder="6-digit Verification Code"
                (keydown.enter)="$event.preventDefault()" />
              @if (retrieveUserNameForm.get('verificationCode')?.hasError('required')) {
                <mat-error>
                  Verification code is required
                </mat-error>
              }
              @if (retrieveUserNameForm.get('verificationCode')?.hasError('invalidCode')) {
                <mat-error>
                  Your verification code is incorrect. Please check it and try again.
                </mat-error>
              }
            </mat-form-field>

            <div class="p-4 text-gray-600 text-sm justify-center w-full">
              <p>
                Your verification code is valid for <strong class="text-blue-600">10 minutes</strong>. If it expires, you
                may request a new one.
              </p>
              <p>
                Please note that you are allowed
                <strong
              [ngClass]="{
                'text-blue-600': attempts > 0 && attempts < 5,
                'text-red-600': attempts === 5,
              }"
                  >{{ 5 - attempts }} more attempts
                </strong>
                to resend the verification code.
              </p>
            </div>

            @if (isLoading) {
              <app-loading class="my-2"></app-loading>
            }
            @defer (on interaction (btnRetrieveUserName)) {
            <app-response-viewer class="my-2" [responseData]="retrieveUserName()"></app-response-viewer>
          }
          @defer (on interaction (btnResendCode)) {
          <app-response-viewer class="my-2" [responseData]="verificationCodeResponse()"></app-response-viewer>
        }
      </div>
      <div class="flex justify-end gap-3">
        <button mat-raised-button matDialogClose="true">Back to Login</button>
        <button
          #btnResendCode
          mat-raised-button
          (click)="onResendCode()"
          [disabled]="
            isLoading ||
            verificationCodeResponse()?.status === maxAttemptsReached ||
            attempts === 5 ||
            retrieveUserName()?.status === maxCheckAttemptsReached
          ">
          Resend Code
        </button>
        <button
          #btnRetrieveUserName
          mat-raised-button
          color="primary"
          (click)="onRetrieveUsername(stepper)"
          [disabled]="
            !retrieveUserNameForm.valid ||
            isLoading ||
            retrieveUserName()?.status === approved ||
            retrieveUserName()?.status === maxCheckAttemptsReached
          ">
          Recover your Username
        </button>
      </div>
    </form>
  </mat-step>

  <mat-step>
    <ng-template matStepLabel>Confirmation</ng-template>

    <div class="p-8 flex flex-col w-full items-center justify-center">
      <p class="label">Your username is:</p>
      <div class="bg-gray-100 p-2 rounded-md shadow-md my-3 bg-gradient-to-r from-blue-500 to-blue-700 w-full relative">
        <!-- Copy Button -->
        <button
          mat-icon-button
          matTooltip="Copy User Name"
          #tooltip="matTooltip"
          (click)="copyToClipboard()"
          class="absolute top-2 right-2 text-gray-300">
          <mat-icon>content_copy</mat-icon>
        </button>

        <!-- User Name -->
        <p class="text-2xl text-white font-bold text-center p-2 rounded-md mt-2" #userName>
          {{ retrieveUserName()?.username }}
        </p>
      </div>
      <p class="text-center text-gray-600 my-6">
        Please try to log in again using your username.
        <br />
        If you encounter any issues, feel free to contact
        <a [routerLink]="['contact']" class="text-blue-500 underline hover:text-blue-700" mat-dialog-close="true">
          customer support</a
          >.
        </p>
      </div>
      <div class="flex justify-end p-5">
        <button mat-raised-button matDialogClose="true">Back to Login</button>
      </div>
    </mat-step>
  </mat-horizontal-stepper>
